import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { EllipsisTooltipDirective } from '../../directives';
import { HxActionGroupComponent } from '../action-group/action-group.component';
import { ActionGroupConfig, ActionItem } from '../action-group/action-group.types';
import { HxCheckbox } from '../checkbox/checkbox';
import { HxIcon } from '../icon/icon';
import { RowsPerPageOption } from '../pagination';
import { HxPagination } from '../pagination/pagination';
import { PaginationEvent } from '../pagination/pagination-event';
import { HxPopoverDirective } from '../popover/popover';
import { HxRadio } from '../radio/radio';
import {
  HxHeaderCell,
  HxTableCellDirective,
  HxTableFooterDirective,
  HxTableHeaderDirective,
  HxTableRowDirective,
} from '../table';
import { HxTableComponent } from '../table/table';

/** Selection mode for the data table */
export type SelectionMode = 'none' | 'single' | 'multiple';

/** Column definition for the data table */
export interface DataTableColumn {
  /** Unique identifier for the column */
  id: string;
  /** Display label for the column header */
  label: string;
  /** Field name in the data object to display */
  field: string;
  /** Optional column width (e.g., '150px', '20%') */
  width?: any;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Custom CSS class for the column */
  className?: string;
  /** Optional custom template for rendering column cells */
  template?: TemplateRef<any>;
  /** Optional transform function to apply to the row value */
  transform?: (row: any, value: any) => any;
  /** Optional declarative action factory generating ActionItems for the row */
  actions?: (row: any) => ActionItem<any>[];
  /** Optional configuration for action group */
  actionGroupConfig?: ActionGroupConfig;
}

/**
 * Harmony Data Table Component (hx-data-table)
 *
 * A simple, input-driven wrapper around hx-table that accepts rows and columns as inputs. Perfect for displaying
 * tabular data without needing complex content projection setup.
 *
 * Features:
 *
 * - Input-based rows and columns configuration
 * - Custom column templates via ng-template projection
 * - Row selection with checkboxes
 * - Server-side sorting (emits events for consumer to handle)
 * - Built-in pagination with HxPagination component (advanced controls)
 * - Hover effects and striped rows
 * - Responsive design using hx-table
 * - WCAG 2.1 AA compliant
 * - Full keyboard navigation
 *
 * @example
 *   Basic usage
 *   ```html
 *   <hx-data-table
 *   [columns]="columns"
 *   [rows]="users"
 *   [selectionMode]="'multiple'"
 *   [enableHover]="true"
 *   (rowSelected)="onRowSelected($event)"
 *   />
 *   ```
 *
 * @example
 *   With custom column templates
 *   ```typescript
 *   columns = [
 *   { id: 'status', label: 'Status', field: 'status', template: this.statusTemplate },
 *   { id: 'actions', label: 'Actions', field: '', template: this.actionsTemplate }
 *   ];
 *   ```
 *   ```html
 *   <ng-template #statusTemplate let-value="value">
 *   <hx-badge [text]="value" [variant]="value === 'Active' ? 'success' : 'secondary'"></hx-badge>
 *   </ng-template>
 *
 *   <ng-template #actionsTemplate let-row="row">
 *   <hx-button [size]="'small'" (click)="editUser(row)">Edit</hx-button>
 *   </ng-template>
 *   ```
 *
 * @example
 *   With pagination and templates
 *   ```typescript
 *   columns = [
 *   { id: 'status', label: 'Status', field: 'status', template: this.statusTemplate }
 *   ];
 *   ```
 *   ```html
 *   <hx-data-table
 *   [columns]="columns"
 *   [rows]="users"
 *   [enablePagination]="true"
 *   [(currentPage)]="currentPage"
 *   [(pageSize)]="pageSize"
 *   [totalRecords]="totalUsers"
 *   (pageChanged)="onPageChange($event)"
 *   />
 *
 *   <ng-template #statusTemplate let-value="value">
 *   <hx-badge [text]="value"></hx-badge>
 *   </ng-template>
 *   ```
 *
 * @example
 *   With server-side sorting
 *   ```typescript
 *   onColumnSort(event: { columnId: string; direction: 'asc' | 'desc' | 'none' }): void {
 *   // Fetch sorted data from server
 *   this.dataService.getSortedUsers(event.columnId, event.direction).subscribe(
 *   users => this.users = users
 *   );
 *   }
 *   ```
 *
 * @example
 *   With transform functions
 *   ```typescript
 *   columns = [
 *   { id: 'name', label: 'Full Name', field: 'firstName', transform: (row) => `${row.firstName} ${row.lastName}` },
 *   { id: 'status', label: 'Status', field: 'status', transform: (row, value) => value.toUpperCase() },
 *   { id: 'date', label: 'Created Date', field: 'createdAt', transform: (row, value) => new Date(value).toLocaleDateString() }
 *   ];
 *   ```
 *   ```html
 *   <hx-data-table
 *   [columns]="columns"
 *   [rows]="users"
 *   />
 *   ```
 */
@Component({
  selector: 'hx-data-table',
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    HxTableComponent,
    HxTableHeaderDirective,
    HxHeaderCell,
    HxTableRowDirective,
    HxTableCellDirective,
    HxPagination,
    HxCheckbox,
    HxRadio,
    HxTableFooterDirective,
    EllipsisTooltipDirective,
    HxIcon,
    HxPopoverDirective,
    HxActionGroupComponent,
  ],
  templateUrl: './data-table.html',
  styleUrl: './data-table.css',
  exportAs: 'hxDataTable',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxDataTableComponent extends HxBaseControl {
  /** Column definitions */
  readonly columns = input.required<DataTableColumn[]>();

  /** Table rows/data */
  readonly rows = input.required<any[]>();

  /** Enable row hover effect */
  readonly enableRowHover = input<boolean>(true);

  /** Selection mode for rows: 'none' (no selection), 'single' (radio button), or 'multiple' (checkbox) */
  readonly selectionMode = input<SelectionMode>('none');

  /** Maximum height for scrolling */
  readonly maxHeight = input<string>('600px');

  readonly resizableColumns = input<boolean>(true);

  /**
   * Whether the footer is fixed/sticky at the bottom. When true, the table will use maxHeight as its height and the
   * footer will remain fixed at the bottom.
   */
  readonly footerFixed = input<boolean>(true);

  /**
   * Row height size for table rows.
   *
   * - 'xs': 28px
   * - 'sm': 32px
   * - 'md': 40px (default)
   * - 'lg': 48px
   */
  readonly rowHeight = input<'xs' | 'sm' | 'md' | 'lg'>('lg');

  /**
   * Visual variant for the table header.
   *
   * - 'default': Borders with vertical divider lines between cells
   * - 'outline': Border around header, no vertical divider lines between cells
   * - 'dividers': No border around header, only vertical divider lines between cells
   * - 'borderless': No borders, no dividers
   */
  readonly headerVariant = input<'default' | 'outline' | 'dividers' | 'borderless'>('default');

  /**
   * Visual variant for the table rows.
   *
   * - 'default': Borders with vertical divider lines between cells
   * - 'outline': Border around rows, no vertical divider lines between cells
   * - 'dividers': No border around rows, only vertical divider lines between cells
   * - 'borderless': No borders, no dividers
   */
  readonly rowVariant = input<'default' | 'outline' | 'dividers' | 'borderless'>('default');

  /**
   * Hover effect variant for table rows.
   *
   * - 'none': No hover effect
   * - 'color': Background color change on hover (default)
   * - 'shadow': Shadow effect on hover
   * - 'focus': Focus ring style on hover
   */
  readonly hoverVariant = input<'none' | 'color' | 'shadow' | 'focus'>('color');

  /** Whether the table is disabled */
  readonly disabled = input<boolean>(false);

  /** Message when no data is available */
  readonly noDataMessage = input<string>('No data available');

  /** Enable pagination */
  readonly enablePagination = input<boolean>(false);

  /** Current page number (1-based) - two-way binding for pagination control */
  readonly currentPage = model<number>(1);

  /** Items per page - two-way binding for pagination control */
  readonly pageSize = model<number>(10);

  /** Total number of records */
  readonly totalRecords = input<number>(0);

  /** Show the total number of records in the pagination footer */
  readonly showTotalRecordCount = input<boolean>(true);

  readonly selectAllColumnWidth = input<string>('5%');

  /** Show a control in the leading header cell for toggling column visibility. */
  readonly enableColumnConfig = input<boolean>(false);

  /**
   * LocalStorage key used to persist hidden columns and drag-resized widths. Leave empty to keep the configuration in
   * memory only.
   */
  readonly columnStateKey = input<string>('');

  /** Columns that must always stay visible (their checkboxes render disabled). */
  readonly lockedColumnIds = input<string[]>([]);

  /** Columns hidden until the user explicitly enables them in the column configuration. */
  readonly defaultHiddenColumnIds = input<string[]>([]);

  /** Heading shown above the column checkboxes. */
  readonly columnConfigTitle = input<string>('Configure columns');

  /**
   * Render the column-config button inside this column's header instead of the leading selection cell. Takes precedence
   * over `columnConfigColumnIndex`. Leave empty to keep it in the leading cell.
   */
  readonly columnConfigColumnId = input<string>('');

  /**
   * Position of the header hosting the column-config button, for tables without a stable column id to target. Negative
   * values count from the end (-1 is the last column). Ignored when `columnConfigColumnId` is set.
   */
  readonly columnConfigColumnIndex = input<number | null>(null);

  readonly pageSizeOptions = input<RowsPerPageOption[]>([
    { id: 'page-size-10', value: 10, label: '10' },
    { id: 'page-size-25', value: 25, label: '25' },
    { id: 'page-size-50', value: 50, label: '50' },
    { id: 'page-size-100', value: 100, label: '100' },
  ]);
  /** Page size options for pagination dropdown */

  // Output events
  readonly rowSelected = output<{ rowIndex: number; row: any }>();
  readonly rowDoubleClicked = output<{ rowIndex: number; row: any }>();
  readonly columnSorted = output<{ columnId: string; direction: 'asc' | 'desc' | 'none' }>();
  readonly pageChanged = output<PaginationEvent>();
  /** Emits all currently selected rows whenever selection changes */
  readonly selectedRowsChanged = output<any[]>();
  /** Emits the ids of the currently visible columns whenever the configuration changes */
  readonly visibleColumnsChanged = output<string[]>();

  // Internal state
  private readonly selectedRowIndices = signal<Set<number>>(new Set());
  readonly allRowsSelectedValues = signal<(string | number)[]>([]);
  private readonly selectedSingleRowIndex = signal<number | null>(null);
  readonly headerCheckboxIndeterminate = signal<boolean>(false);

  private readonly hiddenColumnIds = signal<string[]>([]);
  private readonly columnWidthOverrides = signal<Record<string, string>>({});
  private hasRestoredColumnState = false;

  /** Columns after hiding and width overrides are applied; drives both header and body rendering. */
  readonly visibleColumns = computed<DataTableColumn[]>(() => {
    const hidden = new Set(this.hiddenColumnIds());
    const widths = this.columnWidthOverrides();

    return this.columns()
      .filter((column) => !hidden.has(column.id))
      .map((column) => (widths[column.id] ? { ...column, width: widths[column.id] } : column));
  });

  /** Checkbox model for the configuration popover. */
  readonly columnConfigOptions = computed(() => {
    const hidden = new Set(this.hiddenColumnIds());
    const locked = new Set(this.lockedColumnIds());

    return this.columns().map((column) => ({
      id: column.id,
      label: column.label,
      checked: !hidden.has(column.id),
      locked: locked.has(column.id),
    }));
  });

  /** Whether the user has hidden a column or changed a persisted column width. */
  readonly hasCustomColumnConfig = computed(() => {
    const hidden = new Set(this.hiddenColumnIds());
    const defaults = new Set(this.defaultHiddenColumnIds());
    const hiddenColumnsChanged = hidden.size !== defaults.size || [...hidden].some((id) => !defaults.has(id));
    return hiddenColumnsChanged || Object.keys(this.columnWidthOverrides()).length > 0;
  });

  currentColumnSorted = signal<{ columnId: string; direction: 'asc' | 'desc' } | null>(null);

  /** Check if selection is enabled */
  readonly isSelectionEnabled = computed(() => this.selectionMode() !== 'none');

  /** Id of the column header hosting the config button, or '' when it belongs in the leading cell. */
  readonly columnConfigHostId = computed(() => {
    if (!this.enableColumnConfig()) {
      return '';
    }

    const columns = this.visibleColumns();
    if (columns.length === 0) {
      return '';
    }

    const targetId = this.columnConfigColumnId();
    if (targetId) {
      // Falling back keeps the button reachable if its host column gets hidden.
      return columns.some((column) => column.id === targetId) ? targetId : columns[0].id;
    }

    const index = this.columnConfigColumnIndex();
    if (index === null || index === undefined) {
      return '';
    }

    const resolved = index < 0 ? columns.length + index : index;
    return columns[Math.min(Math.max(resolved, 0), columns.length - 1)].id;
  });

  /** True when the config button has no host column and therefore sits in the leading cell. */
  readonly isColumnConfigInLeadCell = computed(() => this.enableColumnConfig() && !this.columnConfigHostId());

  /** The leading cell only exists for selection, or to host the config button. */
  readonly showLeadColumn = computed(() => this.isSelectionEnabled() || this.isColumnConfigInLeadCell());

  /**
   * Leading column width. The config button sits beside the select-all checkbox, so the column needs a pixel floor or a
   * small percentage width clips it.
   */
  readonly leadColumnWidth = computed(() => {
    const configured = this.selectAllColumnWidth();
    return this.isColumnConfigInLeadCell() ? `max(${configured}, 5rem)` : configured;
  });

  /** Check if multiple selection is enabled */
  readonly isMultipleSelection = computed(() => this.selectionMode() === 'multiple');

  /** Check if single selection is enabled */
  readonly isSingleSelection = computed(() => this.selectionMode() === 'single');

  /** Check if a row is disabled */
  isRowDisabled = (index: number): boolean => {
    const row = this.rows()[index];
    return row && row.isDisabled === true;
  };

  /** Check if a row selection should be hidden */
  isRowSelectionHidden = (index: number): boolean => {
    const row = this.rows()[index];
    return row && row.hideSelection === true;
  };

  /** Check if a row is selected */
  isRowSelected = (index: number): boolean => {
    if (this.isSingleSelection()) {
      return this.selectedSingleRowIndex() === index;
    }
    return this.selectedRowIndices().has(index);
  };

  /** Get selected values for header checkbox (select all) */
  getHeaderCheckboxValues = (): (string | number)[] => {
    const enabledRowIndices = this.rows()
      .map((_, idx) => idx)
      .filter((idx) => !this.isRowDisabled(idx) && !this.isRowSelectionHidden(idx));
    if (enabledRowIndices.length === 0) return [];
    const allSelected = enabledRowIndices.every((idx) => this.isRowSelected(idx));
    return allSelected ? ['all'] : [];
  };

  /** Get header checkbox options with indeterminate state */
  readonly headerCheckboxOptions = computed(() => {
    return [
      {
        label: '',
        value: 'all' as const,
        indeterminate: this.headerCheckboxIndeterminate(),
      },
    ];
  });

  /** Get selected values for row checkboxes */
  getRowCheckboxValues = (index: number): (string | number)[] => {
    return this.isRowSelected(index) ? ['select-row'] : [];
  };

  /** Get checked state for row radio button */
  isRowRadioChecked = (index: number): boolean => {
    return this.selectedSingleRowIndex() === index;
  };

  /** Computed list of all selected rows */
  readonly selectedRows = computed(() => {
    if (this.isSingleSelection()) {
      const index = this.selectedSingleRowIndex();
      return index !== null ? [this.rows()[index]] : [];
    }
    return Array.from(this.selectedRowIndices()).map((idx) => this.rows()[idx]);
  });

  /** Handle select all rows */
  onSelectAllRows = (selectedValues: (string | number)[]): void => {
    const indices = new Set(this.selectedRowIndices());

    // Get only enabled and visible-selection rows
    const enabledRowIndices = this.rows()
      .map((_, idx) => idx)
      .filter((idx) => !this.isRowDisabled(idx) && !this.isRowSelectionHidden(idx));

    if (selectedValues.length > 0) {
      // Select all enabled rows
      enabledRowIndices.forEach((idx) => {
        indices.add(idx);
      });
      this.allRowsSelectedValues.set(['all']);
      this.headerCheckboxIndeterminate.set(false);
    } else {
      // Deselect all enabled rows
      enabledRowIndices.forEach((idx) => {
        indices.delete(idx);
      });
      this.allRowsSelectedValues.set([]);
      this.headerCheckboxIndeterminate.set(false);
    }

    this.selectedRowIndices.set(indices);
    this.selectedRowsChanged.emit(this.selectedRows());
  };

  /** Compute total pages */
  readonly totalPages = computed(() => {
    if (!this.enablePagination()) return 1;
    const size = Math.max(1, this.pageSize());
    return Math.ceil(this.totalRecords() / size);
  });

  /** Computed display rows - directly uses server-paginated rows */
  readonly displayRows = computed(() => {
    return this.rows();
  });

  /**
   * Effect to clear selections when rows change (e.g., pagination, filtering, sorting). This prevents stale selections
   * from persisting when the data set changes.
   */
  private readonly clearSelectionsOnRowsChange = effect(() => {
    // Track rows to detect changes
    this.rows();

    // Clear all selection state
    this.selectedRowIndices.set(new Set());
    this.selectedSingleRowIndex.set(null);
    this.allRowsSelectedValues.set([]);
    this.headerCheckboxIndeterminate.set(false);
  });

  /** Get value from row by field name and apply optional transform function */
  getRowValue(row: any, field: string, column?: DataTableColumn): any {
    const value = row?.[field] ?? '';
    if (column?.transform) {
      return column.transform(row, value);
    }
    return value;
  }

  /** Show or hide a column; locked columns and the last visible column are ignored. */
  toggleColumn(columnId: string): void {
    if (this.lockedColumnIds().includes(columnId)) {
      return;
    }

    const hidden = new Set(this.hiddenColumnIds());

    if (hidden.has(columnId)) {
      hidden.delete(columnId);
    } else {
      // Hiding everything would leave an unusable table.
      if (this.columns().length - hidden.size <= 1) {
        return;
      }
      hidden.add(columnId);
    }

    this.hiddenColumnIds.set([...hidden]);
    this.persistColumnState();
    this.visibleColumnsChanged.emit(this.visibleColumns().map((column) => column.id));
  }

  /** Restore the default column visibility and clear stored widths. */
  resetColumnConfig(): void {
    const defaults = this.defaultHiddenColumnIds();
    this.hiddenColumnIds.set([...defaults]);
    this.columnWidthOverrides.set({});
    this.persistColumnState();
    const hidden = new Set(defaults);
    this.visibleColumnsChanged.emit(
      this.columns()
        .filter((column) => !hidden.has(column.id))
        .map((column) => column.id)
    );
  }

  /** Map the grid widths reported after a drag-resize back onto column ids. */
  protected onColumnsResized(widths: string[]): void {
    const leadingCells = this.isSelectionEnabled() ? 1 : 0;
    const columnWidths = widths.slice(leadingCells);
    const visible = this.visibleColumns();
    const next = { ...this.columnWidthOverrides() };

    visible.forEach((column, index) => {
      const width = columnWidths[index];
      if (width) {
        next[column.id] = width;
      }
    });

    this.columnWidthOverrides.set(next);
    this.persistColumnState();
  }

  /** Reads persisted state once, lazily, so the key input is available. */
  private readonly restoreColumnStateEffect = effect(() => {
    const key = this.columnStateKey();

    if (this.hasRestoredColumnState) {
      return;
    }

    untracked(() => {
      this.hasRestoredColumnState = true;
      this.hiddenColumnIds.set([...this.defaultHiddenColumnIds()]);

      if (!key || typeof localStorage === 'undefined') {
        return;
      }

      try {
        const raw = localStorage.getItem(key);
        if (!raw) {
          return;
        }

        const parsed = JSON.parse(raw) as {
          hiddenColumnIds?: string[];
          columnWidths?: Record<string, string>;
        };

        if (Array.isArray(parsed.hiddenColumnIds)) {
          this.hiddenColumnIds.set(parsed.hiddenColumnIds.filter((id) => typeof id === 'string'));
        }

        if (parsed.columnWidths && typeof parsed.columnWidths === 'object') {
          this.columnWidthOverrides.set(parsed.columnWidths);
        }
      } catch {
        // Corrupt or unavailable storage should never break rendering.
      }
    });
  });

  private persistColumnState(): void {
    const key = this.columnStateKey();
    if (!key || typeof localStorage === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          hiddenColumnIds: this.hiddenColumnIds(),
          columnWidths: this.columnWidthOverrides(),
        })
      );
    } catch {
      // Storage may be full or blocked; configuration still applies for this session.
    }
  }

  /** Get template for a column */
  getColumnTemplate(column: DataTableColumn): TemplateRef<any> | undefined {
    return column.template;
  }

  /** Handle row selection from HxCheckbox */
  onRowCheckboxChange = (index: number, selectedValues: (string | number)[]): void => {
    if (!this.isMultipleSelection()) return;
    if (this.isRowDisabled(index)) return;

    const isChecked = selectedValues.length > 0;

    if (isChecked) {
      this.selectedRowIndices().add(index);
    } else {
      this.selectedRowIndices().delete(index);
    }

    this.selectedRowIndices.set(new Set(this.selectedRowIndices()));

    // Update header checkbox state (including indeterminate)
    const enabledRowIndices = this.rows()
      .map((_, idx) => idx)
      .filter((idx) => !this.isRowDisabled(idx) && !this.isRowSelectionHidden(idx));
    const selectedEnabledCount = enabledRowIndices.filter((idx) => this.selectedRowIndices().has(idx)).length;
    const totalEnabledCount = enabledRowIndices.length;

    if (selectedEnabledCount === totalEnabledCount && totalEnabledCount > 0) {
      this.allRowsSelectedValues.set(['all']);
      this.headerCheckboxIndeterminate.set(false);
    } else if (selectedEnabledCount > 0) {
      this.allRowsSelectedValues.set([]);
      this.headerCheckboxIndeterminate.set(true);
    } else {
      this.allRowsSelectedValues.set([]);
      this.headerCheckboxIndeterminate.set(false);
    }

    this.rowSelected.emit({
      rowIndex: index,
      row: this.rows()[index],
    });
    this.selectedRowsChanged.emit(this.selectedRows());
  };

  /** Handle row selection from radio button */
  onRowRadioChange = (event: any): void => {
    if (!this.isSingleSelection()) return;

    const index = event.value;
    if (this.isRowDisabled(index)) return;

    this.selectedSingleRowIndex.set(index);
    this.rowSelected.emit({
      rowIndex: index,
      row: this.rows()[index],
    });
    this.selectedRowsChanged.emit(this.selectedRows());
  };

  /** Handle row selection checkbox change */
  selectRow = (index: number, event?: Event): void => {
    if (event) {
      event.stopPropagation();
    }

    if (this.isRowDisabled(index)) return;

    const selected = this.selectedRowIndices();
    if (selected.has(index)) {
      selected.delete(index);
    } else {
      selected.add(index);
    }

    this.selectedRowIndices.set(new Set(selected));
    this.rowSelected.emit({
      rowIndex: index,
      row: this.rows()[index],
    });
    this.selectedRowsChanged.emit(this.selectedRows());
  };

  /** Handle row double click */
  onRowDoubleClick = (index: number): void => {
    this.rowDoubleClicked.emit({
      rowIndex: index,
      row: this.rows()[index],
    });
  };

  /** Handle column sort - emits event for server-side sorting */
  onColumnSort = (column: DataTableColumn): void => {
    if (!column.sortable) return;
    let nextDirection: 'asc' | 'desc' | null = 'asc';
    if (this.currentColumnSorted() && this.currentColumnSorted().columnId === column.id) {
      const currentDirection = this.currentColumnSorted().direction;
      if (currentDirection === 'asc') {
        nextDirection = 'desc';
      }
    }
    this.currentColumnSorted.set({ columnId: column.id, direction: nextDirection });
    this.columnSorted.emit({
      columnId: column.id,
      direction: nextDirection,
    });
  };

  /** Handle page change from pagination component */
  onPaginationPageChange = (event: PaginationEvent): void => {
    this.currentPage.set(event.newPage);
    this.pageChanged.emit(event);
  };

  /** Handle page size change from pagination component */
  onPaginationPageSizeChange = (event: PaginationEvent): void => {
    this.pageSize.set(event.newPageSize);
    this.pageChanged.emit(event);
  };
}
